import { Group, Stack, Text, Tooltip } from '@mantine/core';
import { Folder } from '../types';
import { FilterField } from '@/users/pages/UserFilters/types/FilterField';
import { myDate } from '@/commons/myDate';
import { useLocale } from '@/contexts';
import { useRef, useEffect, useState } from 'react';

interface RenderFieldsProps {
  folder: Folder;
  customFieldBasedFilters: FilterField[];
  /**
   * When true, render every filter-definition field (empty shows "-").
   * When false, only fields that exist on the folder are shown (legacy client-side column filter).
   */
  showAllCustomFields?: boolean;
}

interface FieldWithTooltipProps {
  label: string;
  value: string;
  textSize: string;
  hasValue: boolean;
}

const FieldWithTooltip = ({
  label,
  value,
  textSize,
  hasValue,
}: FieldWithTooltipProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const labelMeasureRef = useRef<HTMLDivElement>(null);
  const valueMeasureRef = useRef<HTMLDivElement>(null);
  const [showLabelTooltip, setShowLabelTooltip] = useState(false);
  const [showValueTooltip, setShowValueTooltip] = useState(false);

  useEffect(() => {
    if (labelMeasureRef.current && valueMeasureRef.current) {
      // Measure actual width of both label and value
      const labelWidth = labelMeasureRef.current.offsetWidth;
      const valueWidth = valueMeasureRef.current.offsetWidth;
      const totalWidth = Math.max(labelWidth, valueWidth);
      setShowLabelTooltip(totalWidth > 200);
      // Only show tooltip for value if the value itself is big
      setShowValueTooltip(valueWidth > 200);
    }
  }, [label, value]);

  const textElement = (
    <Text
      ref={textRef}
      size={textSize}
      c={hasValue ? undefined : 'dimmed'}
      style={{
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Text>
  );

  const labelElement = (
    <Text
      size="xs"
      fw={500}
      c="dimmed"
      style={{
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Text>
  );

  return (
    <Stack gap={2} style={{ position: 'relative' }}>
      {/* Hidden elements to measure actual text width */}
      <Text
        ref={labelMeasureRef}
        size="xs"
        fw={500}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          top: '-9999px',
          left: '-9999px',
        }}
      >
        {label}
      </Text>
      <Text
        ref={valueMeasureRef}
        size={textSize}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          top: '-9999px',
          left: '-9999px',
        }}
      >
        {value}
      </Text>
      {showLabelTooltip ? (
        <Tooltip label={label} withArrow>
          {labelElement}
        </Tooltip>
      ) : (
        labelElement
      )}
      {showValueTooltip ? (
        <Tooltip label={value} withArrow>
          {textElement}
        </Tooltip>
      ) : (
        textElement
      )}
    </Stack>
  );
};

export const RenderFields = ({
  folder,
  customFieldBasedFilters,
  showAllCustomFields = false,
}: RenderFieldsProps) => {
  const { locale } = useLocale();

  // Convert locale format for myDate (e.g., 'en' -> 'en-US', 'ro' -> 'ro-RO')
  const dateLocale = locale === 'en' ? 'en-US' : 'ro-RO';

  const fieldExists = (customField: FilterField): boolean => {
    // Check if this field exists in any file of the folder
    // Split name by '.' and use left part for all field types
    let fieldNameToFind = customField.name;
    if (customField.name.includes('.')) {
      fieldNameToFind = customField.name.split('.')[0];
    }

    return (
      folder.files?.some((file: any) => {
        return file.fields?.some((f: any) => f.name === fieldNameToFind);
      }) || false
    );
  };

  const getFieldValue = (customField: FilterField): string | null => {
    // Split name by '.' and use left part for all field types
    let fieldNameToFind = customField.name;
    if (customField.name.includes('.')) {
      fieldNameToFind = customField.name.split('.')[0];
    }

    // Search through all files in the folder for this field
    for (const file of folder.files) {
      const field = file.fields?.find((f: any) => f.name === fieldNameToFind);
      if (field && field.value != null) {
        // If no path, return primitive value directly
        if (!customField.path || customField.path === '') {
          // Handle checkboxGroup fields where value is an array
          // Check both customField.type and field.type to handle checkboxGroup
          const isCheckboxGroup =
            customField.type === 'checkboxGroup' ||
            field.type === 'checkboxGroup';

          if (isCheckboxGroup && Array.isArray(field.value)) {
            if (field.value.length === 0) return null;
            // Map IDs to labels if items are available
            if (customField.items && customField.items.length > 0) {
              const labels = field.value
                .map((id: string) => {
                  const item = customField.items?.find((i) => i.id === id);
                  return item ? item.label : id;
                })
                .filter(Boolean);
              return labels.length > 0 ? labels.join(', ') : null;
            }
            return field.value.join(', ');
          }

          // If it's a checkboxGroup but value is not an array, try to handle it
          if (isCheckboxGroup && !Array.isArray(field.value)) {
            // Maybe it's stored as a string or something else
            return field.value ? String(field.value) : null;
          }

          return field.value ? String(field.value) : null;
        }

        // If path exists, navigate the object
        if (field.value && typeof field.value === 'object') {
          const pathParts = customField.path.split('.');
          let value: any = field.value;

          for (const part of pathParts) {
            if (value != null && typeof value === 'object' && part in value) {
              value = value[part];
            } else {
              return null;
            }
          }

          // Handle checkboxGroup fields where value is an array
          // Check both customField.type and field.type to handle checkboxGroup
          if (
            (customField.type === 'checkboxGroup' ||
              field.type === 'checkboxGroup') &&
            Array.isArray(value)
          ) {
            if (value.length === 0) return null;
            // Map IDs to labels if items are available
            if (customField.items && customField.items.length > 0) {
              const labels = value
                .map((id: string) => {
                  const item = customField.items?.find((i) => i.id === id);
                  return item ? item.label : id;
                })
                .filter(Boolean);
              return labels.length > 0 ? labels.join(', ') : null;
            }
            return value.join(', ');
          }

          return value != null ? String(value) : null;
        }
      }
    }
    return null;
  };

  const visibleCustomFields = showAllCustomFields
    ? customFieldBasedFilters
    : customFieldBasedFilters.filter((customField) => fieldExists(customField));

  if (visibleCustomFields.length === 0) {
    return null;
  }

  return (
    <Group gap="md">
      {visibleCustomFields.map((customField, index) => {
        const value = getFieldValue(customField);

        // Format date fields using myDate
        const displayValue =
          customField.type === 'datepicker' && value
            ? myDate(value, dateLocale)
            : value || '-';

        // Determine text size based on position
        const textSize =
          index === 0 ? 'lg' : index === 1 ? 'md' : index === 2 ? 'sm' : 'xs';

        return (
          <FieldWithTooltip
            key={`${customField.name}:${customField.path}:${customField.type}:${index}`}
            label={customField.label}
            value={displayValue}
            textSize={textSize}
            hasValue={!!value}
          />
        );
      })}
    </Group>
  );
};
